import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  ListPlus,
  Megaphone,
  MessageSquare,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/Shared/Button';
import { Badge } from '@/components/Shared/Badge';
import { Loading } from '@/components/Shared/Loading';
import { EmptyState } from '@/components/Shared/EmptyState';
import { ConfirmDialog } from '@/components/Shared/ConfirmDialog';
import { SlideOver } from '@/components/Shared/SlideOver';
import { Tabs, TabsList, TabsTrigger } from '@/components/Shared/Tabs';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '@/components/Shared/Table';
import { useAuthStore } from '@/store/useAuthStore';
import { useObjectStore } from '@/store/useObjectStore';
import { useOpenItemsStore } from '@/store/useOpenItemsStore';
import type { OpenItemDto, OpenItemScope } from '@/store/useOpenItemsStore';
import { useServiceStore } from '@/store/useServiceStore';
import { formatDate, isoDate, parseUtcTimestamp } from '@/utils/dateUtils';
import { TaskForm } from '@/pages/Tasks/components/TaskForm';
import OpenItemForm from '@/pages/OpenItems/OpenItemForm';
import OpenItemMessages from '@/pages/OpenItems/OpenItemMessages';
import ReportCreateModal from '@/pages/OpenItems/ReportCreateModal';
import {
  formatDueDate,
  getOpenItemLink,
  getOpenItemText,
  isOverdue,
  isReport,
  isSystemItem,
} from '@/pages/OpenItems/openItemText';

const ICON_CLASS = 'w-3.5 h-3.5 sm:w-4 sm:h-4';
const TASK_DESCRIPTION_MAX = 2000;

/** Local calendar day a report was submitted; the API sends UTC without a zone suffix. */
function formatReportedOn(item: OpenItemDto): string {
  const created = parseUtcTimestamp(item.createdAt);
  return created ? formatDate(created) : '';
}

/**
 * Offene Punkte: eigene Notizen, Meldungen an die Verwaltung und Punkte, die das System aus
 * anderen Modulen ableitet.
 */
export default function OpenItemsPage() {
  const { t, i18n } = useTranslation(['openItems', 'common']);
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { items, scope, includeCompleted, loading, saving, error, fetchOpenItems, setIncludeCompleted, completeOpenItem, reopenOpenItem, deleteOpenItem, convertReport } =
    useOpenItemsStore();
  const objects = useObjectStore((s) => s.objects);
  const fetchAllObjects = useObjectStore((s) => s.fetchAllObjects);
  const services = useServiceStore((s) => s.services);
  const fetchServices = useServiceStore((s) => s.fetchServices);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [editing, setEditing] = useState<OpenItemDto | null>(null);
  const [deleting, setDeleting] = useState<OpenItemDto | null>(null);
  // The report a task is being created from ("Aufgabe erstellen").
  const [converting, setConverting] = useState<OpenItemDto | null>(null);
  // Id, not the item: the list refreshes underneath (counts, completion) and the view follows it.
  const [messagesForId, setMessagesForId] = useState<string | null>(null);
  const messagesItem = useMemo(
    () => (messagesForId ? (items.find((i) => i.id === messagesForId) ?? null) : null),
    [items, messagesForId],
  );
  const todayIso = useMemo(() => isoDate(new Date()), []);

  // Non-admins see their own items and reports; a stale 'all' from an admin session must not leak.
  const allowedScope = (value: OpenItemScope): OpenItemScope =>
    isAdmin || value !== 'all' ? value : 'mine';
  const effectiveScope = allowedScope(scope);
  const showsReports = effectiveScope === 'reports';

  // Initial load only — later scope changes go through the tab handler. Reading the scope from
  // the store (not the render value) keeps this effect from refetching on every tab switch.
  useEffect(() => {
    const storedScope = useOpenItemsStore.getState().scope;
    fetchOpenItems(isAdmin || storedScope !== 'all' ? storedScope : 'mine');
  }, [fetchOpenItems, isAdmin]);

  // TaskForm needs objects and services; load them only once someone converts a report.
  useEffect(() => {
    if (!converting) return;
    fetchAllObjects();
    fetchServices();
  }, [converting, fetchAllObjects, fetchServices]);

  const openCreate = () => {
    if (showsReports) {
      setIsReportOpen(true);
      return;
    }
    setEditing(null);
    setIsFormOpen(true);
  };

  /** The report as the task's description: text, details, who reported it and when. */
  const describeReport = (item: OpenItemDto): string => {
    const reportedOn = formatReportedOn(item);
    const origin = t('openItems:report.taskOrigin', { name: item.createdByName ?? '', date: reportedOn });
    return [item.title, item.note, origin]
      .filter(Boolean)
      .join('\n\n')
      .slice(0, TASK_DESCRIPTION_MAX);
  };

  const openEdit = (item: OpenItemDto) => {
    setEditing(item);
    setIsFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting?.id) return;
    const deleted = await deleteOpenItem(deleting.id);
    if (deleted) setDeleting(null);
  };

  const createButton = (
    <Button
      onClick={openCreate}
      leftIcon={
        showsReports ? (
          <Megaphone className="w-4 h-4" strokeWidth={2} />
        ) : (
          <Plus className="w-4 h-4" strokeWidth={2} />
        )
      }
      className="w-full sm:w-auto"
    >
      {showsReports ? t('openItems:report.create') : t('openItems:actions.create')}
    </Button>
  );

  const renderActions = (item: OpenItemDto) => {
    const link = getOpenItemLink(item);
    if (link) {
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(link)}
          rightIcon={<ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />}
        >
          {t('openItems:actions.open')}
        </Button>
      );
    }
    return (
      <>
        {item.canConvert && (
          <Button
            variant="ghost"
            size="sm"
            title={t('openItems:report.createTask')}
            aria-label={t('openItems:report.createTask')}
            className="p-1 sm:p-2"
            onClick={() => setConverting(item)}
          >
            <ListPlus className={`${ICON_CLASS} text-[#00A871]`} strokeWidth={2} />
          </Button>
        )}
        {isAdmin && item.convertedTaskId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/tasks/${item.convertedTaskId}`)}
            rightIcon={<ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t('openItems:report.openTask')}
          </Button>
        )}
        {/* Everyone taking part may write — the assignee too, who cannot edit the item, and the
            reporter, who cannot complete a report. */}
        {(item.canComplete || isReport(item)) && item.id && (
          <MessagesButton
            label={t('openItems:thread.open')}
            messageCount={item.messageCount ?? 0}
            unreadCount={item.unreadMessageCount ?? 0}
            onClick={() => setMessagesForId(item.id ?? null)}
          />
        )}
        {item.canEdit && renderEditActions(item)}
      </>
    );
  };

  const renderEditActions = (item: OpenItemDto) => {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          title={t('openItems:actions.edit')}
          aria-label={t('openItems:actions.edit')}
          className="p-1 sm:p-2"
          onClick={() => openEdit(item)}
        >
          <Pencil className={ICON_CLASS} strokeWidth={2} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          title={t('openItems:actions.delete')}
          aria-label={t('openItems:actions.delete')}
          className="p-1 sm:p-2"
          onClick={() => setDeleting(item)}
        >
          <Trash2 className={`${ICON_CLASS} text-red-600`} strokeWidth={2} />
        </Button>
      </>
    );
  };

  /** Status of a report as its reporter follows it: open, done, or turned into a task. */
  const renderReportStatus = (item: OpenItemDto) => {
    if (item.convertedTaskId) return <Badge variant="success">{t('openItems:report.status.converted')}</Badge>;
    if (item.completedAt) return <Badge variant="success">{t('openItems:report.status.done')}</Badge>;
    return <Badge variant="warning">{t('openItems:report.status.open')}</Badge>;
  };

  const renderRow = (item: OpenItemDto) => {
    const system = isSystemItem(item);
    const report = isReport(item);
    const overdue = isOverdue(item.dueDate, todayIso);
    const completed = !!item.completedAt;
    const reportedBy = report
      ? t('openItems:report.reportedBy', {
          name: item.createdByName ?? '',
          date: formatReportedOn(item),
        })
      : null;
    const assignee = report ? t('openItems:report.office') : item.assigneeName;
    return (
      <TableRow key={item.id}>
        <TableCell className="w-10">
          {item.canComplete && item.id && (
            completed ? (
              <ReopenButton
                label={t('openItems:actions.reopen')}
                onClick={() => reopenOpenItem(item.id ?? '')}
              />
            ) : (
              <CompleteButton
                label={t('openItems:actions.complete')}
                onClick={() => completeOpenItem(item.id ?? '')}
              />
            )
          )}
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`text-[13px] font-medium ${
                  completed ? 'text-slate-400 line-through' : 'text-slate-900'
                }`}
              >
                {getOpenItemText(item, t, i18n.language)}
              </span>
              {system && <Badge variant="info">{t('openItems:badge.system')}</Badge>}
              {report && <Badge color="purple">{t('openItems:badge.report')}</Badge>}
              {/* The reporter follows the status; Verwaltung sees it through the tick, except a conversion. */}
              {report && (!item.canComplete || item.convertedTaskId) && renderReportStatus(item)}
            </div>
            {!system && item.note && (
              <span className="text-[12px] text-slate-500 truncate max-w-md">{item.note}</span>
            )}
            {(item.objectName || reportedBy) && (
              <span className="text-[11px] text-slate-500">
                {[item.objectName, reportedBy].filter(Boolean).join(' · ')}
              </span>
            )}
            {assignee && <span className="text-[11px] text-slate-500 sm:hidden">{assignee}</span>}
          </div>
        </TableCell>
        <TableCell className="hidden sm:table-cell text-[13px] text-slate-600">
          {assignee || '–'}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          <span
            className={`text-[13px] tabular-nums ${
              overdue ? 'text-red-600 font-medium' : 'text-slate-600'
            }`}
          >
            {formatDueDate(item.dueDate)}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1 sm:gap-2">{renderActions(item)}</div>
        </TableCell>
      </TableRow>
    );
  };

  const renderContent = () => {
    if (loading && items.length === 0) {
      return (
        <div className="p-4">
          <Loading />
        </div>
      );
    }
    if (error) {
      return (
        <div className="p-4">
          <EmptyState variant="error" title={t(error)} onRetry={() => fetchOpenItems()} />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="p-4">
          <EmptyState
            icon={<CheckCircle2 className="w-12 h-12" strokeWidth={1.75} />}
            title={showsReports ? t('openItems:report.empty') : t('openItems:empty')}
            description={showsReports ? t('openItems:report.emptyHint') : t('openItems:emptyHint')}
          />
        </div>
      );
    }
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader className="w-10">
              <span className="sr-only">{t('openItems:actions.complete')}</span>
            </TableHeader>
            <TableHeader>{t('openItems:fields.text')}</TableHeader>
            <TableHeader className="hidden sm:table-cell">
              {t('openItems:fields.assignee')}
            </TableHeader>
            <TableHeader>{t('openItems:fields.due')}</TableHeader>
            <TableHeader className="text-right">{t('common:actions.title')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>{items.map(renderRow)}</TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('openItems:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('openItems:subtitle')}</p>
          </div>
          {createButton}
        </div>
        <Tabs
          value={effectiveScope}
          onValueChange={(value) => fetchOpenItems(allowedScope(value as OpenItemScope))}
          className="mt-3"
        >
          <TabsList>
            <TabsTrigger value="mine">{t('openItems:tabs.mine')}</TabsTrigger>
            {isAdmin && <TabsTrigger value="all">{t('openItems:tabs.all')}</TabsTrigger>}
            <TabsTrigger value="reports">
              {isAdmin ? t('openItems:tabs.reports') : t('openItems:tabs.myReports')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-600">
          <input
            type="checkbox"
            checked={includeCompleted}
            onChange={(e) => setIncludeCompleted(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
          />
          {t('openItems:filters.showCompleted')}
        </label>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">{renderContent()}</div>

      <OpenItemForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} item={editing} />

      <ReportCreateModal isOpen={isReportOpen} onClose={() => setIsReportOpen(false)} />

      <SlideOver
        isOpen={converting !== null}
        onClose={() => setConverting(null)}
        title={t('openItems:report.createTask')}
      >
        {converting && (
          <TaskForm
            key={converting.id}
            onClose={() => setConverting(null)}
            objects={objects}
            services={services}
            prefill={{
              objectId: converting.objectId ?? undefined,
              name: converting.title ?? undefined,
              description: describeReport(converting),
            }}
            onCreated={async (task) => {
              if (converting.id) await convertReport(converting.id, task.taskId);
            }}
          />
        )}
      </SlideOver>

      <OpenItemMessages item={messagesItem} onClose={() => setMessagesForId(null)} />

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('openItems:messages.deleteTitle')}
        message={t('openItems:messages.deleteConfirm')}
        confirmText={t('common:actions.delete')}
        cancelText={t('common:actions.cancel')}
        variant="danger"
        loading={saving}
      />
    </div>
  );
}

interface CompleteButtonProps {
  label: string;
  onClick: () => void;
}

interface MessagesButtonProps {
  label: string;
  messageCount: number;
  unreadCount: number;
  onClick: () => void;
}

/**
 * Speech bubble that opens the message thread: amber with a small count while something is
 * unread, plain amber once there is a thread, grey while there is none.
 */
function MessagesButton({ label, messageCount, unreadCount, onClick }: MessagesButtonProps) {
  const color = messageCount > 0 ? 'text-amber-600' : 'text-slate-400';
  return (
    <Button
      variant="ghost"
      size="sm"
      title={label}
      aria-label={unreadCount > 0 ? `${label} (${unreadCount})` : label}
      className="relative p-1 sm:p-2"
      onClick={onClick}
    >
      <MessageSquare
        className={`${ICON_CLASS} ${color}`}
        strokeWidth={2}
        fill={unreadCount > 0 ? 'currentColor' : 'none'}
      />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold leading-4 text-center tabular-nums">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </Button>
  );
}

/** Filled check for a completed item — one click reopens it. */
function ReopenButton({ label, onClick }: CompleteButtonProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      title={label}
      aria-label={label}
      className="p-1 sm:p-2"
      onClick={onClick}
    >
      <CheckCircle2 className={`${ICON_CLASS} text-emerald-600`} strokeWidth={2} />
    </Button>
  );
}

/** Empty circle that turns into a check on hover — one click marks the item done. */
function CompleteButton({ label, onClick }: CompleteButtonProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      title={label}
      aria-label={label}
      className="group p-1 sm:p-2"
      onClick={onClick}
    >
      <Circle
        className={`${ICON_CLASS} text-slate-400 group-hover:hidden`}
        strokeWidth={2}
      />
      <CheckCircle2
        className={`${ICON_CLASS} text-emerald-600 hidden group-hover:block`}
        strokeWidth={2}
      />
    </Button>
  );
}
